import { alpha, Box, Stack, TextFieldProps, Typography } from '@mui/material'
import { ChangeEvent, DragEvent, FC, useCallback, useRef, useState } from 'react'

import { Icon } from '@/components/icon.tsx'

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function switchCountFiles(count: number) {
  if (
    count === 11 ||
    count === 12 ||
    count === 13 ||
    count === 14 ||
    count === 15 ||
    count === 16 ||
    count === 17 ||
    count === 18 ||
    count === 19
  ) {
    return 'файлов'
  }

  switch (count % 10) {
    case 1:
      return 'файл'
    case 2:
      return 'файла'
    case 3:
      return 'файла'
    case 4:
      return 'файла'
    default:
      return 'файлов'
  }
}

function acceptToText(accept: string) {
  return accept.split('.').join(' ').toUpperCase()
}

export interface FileInputProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'onError'> {
  accept?: string
  onChange?: (files: File | File[] | null) => void
  value?: File | File[] | null
  max?: number
  onFileRemove?: (file: File, index: number) => void
  onError?: (error: Error) => void
}

export const FileField: FC<FileInputProps> = ({
  accept,
  max = 1,
  onChange,
  value,
  fullWidth = true,
  placeholder = 'Файл',
  onError,
  onFileRemove,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }

      const fileArray = Array.from(files)

      if (max > 1) {
        if (Array.isArray(value) && value.length > 0) {
          if (max && value.length + fileArray.length > max) {
            return
          }
          onChange?.([...value, ...fileArray])
        } else {
          onChange?.(fileArray)
        }
      } else {
        onChange?.(fileArray[0] || null)
      }

      event.target.value = ''
    },
    [onChange, max]
  )

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (!files || files.length === 0) return

      const fileArray = Array.from(files)

      if (accept) {
        const acceptedTypes = accept.split(',').map((t) => t.trim())
        const invalidFiles: File[] = []
        const validFiles: File[] = []

        fileArray.forEach((file) => {
          const isValid = acceptedTypes.some((type) => {
            if (type.includes('/*')) {
              const typePrefix = type.split('/')[0]
              return file.type.startsWith(typePrefix + '/')
            }
            const extension = file.name.split('.').pop()?.toLowerCase()
            return type.toLowerCase() === `.${extension}` || type.toLowerCase() === file.type
          })

          if (isValid) {
            validFiles.push(file)
          } else {
            invalidFiles.push(file)
          }
        })

        if (invalidFiles.length > 0) {
          const invalidNames = invalidFiles.map((f) => f.name).join(', ')
          onError?.(new Error(`Недопустимые форматы файлов: ${invalidNames}`))
          if (validFiles.length === 0) {
            return
          }
        }

        const validFileArray = validFiles

        if (max > 1 && Array.isArray(value) && value.length + validFileArray.length > max) {
          return
        }

        if (max > 1) {
          if (Array.isArray(value) && value.length > 0) {
            onChange?.([...value, ...validFileArray])
          } else {
            onChange?.(validFileArray)
          }
        } else {
          onChange?.(validFileArray[0] || null)
        }
      } else {
        if (max > 1 && Array.isArray(value) && value.length + fileArray.length > max) {
          return
        }

        if (max > 1) {
          if (Array.isArray(value) && value.length > 0) {
            onChange?.([...value, ...fileArray])
          } else {
            onChange?.(fileArray)
          }
        } else {
          onChange?.(fileArray[0] || null)
        }
      }
    },
    [max, value, onChange, accept, onError]
  )

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (!value) return

      if (Array.isArray(value)) {
        const newFiles = value.filter((_, i) => i !== index)
        onChange?.(newFiles.length > 0 ? newFiles : null)

        onFileRemove?.(value[index], index)
      } else {
        onChange?.(null)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
        onFileRemove?.(value, 0)
      }
    },
    [value, onChange, onFileRemove]
  )

  const getFileList = (): File[] => {
    if (!value) return []
    if (Array.isArray(value)) return value
    return [value]
  }

  const fileList = getFileList()
  const hasFiles = !!value && (Array.isArray(value) ? value.length > 0 : true)

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={max > 1}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {fileList.length < max && (
        <Box
          data-test-id="file-field-click"
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={(theme) => ({
            position: 'relative',
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
            borderRadius: '8px',
            p: 3,
            minHeight: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDragging
              ? alpha(theme.palette.primary.main, 0.25)
              : alpha(theme.palette.primary.main, 0.1),
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.25),
            },
          })}
        >
          <Stack spacing={2}>
            <Stack spacing={1} alignItems="center">
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  textAlign: 'center',
                  fontWeight: hasFiles ? 500 : 400,
                }}
              >
                {isDragging ? 'Отпутите для загрузки' : placeholder}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {!isDragging &&
                  accept &&
                  (max > 1 ? `Максимум ${max} ${switchCountFiles(max)} формата` : 'Один файл формата') +
                    acceptToText(accept)}
              </Typography>
            </Stack>
            <Stack spacing={2} alignItems="center" justifyContent="center" direction="row">
              <Icon name="Attach" size="lg" colorScheme="primary.main" />
              <Typography variant="body2" color="primary" fontWeight={600}>
                Прикрепить файл
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}

      {hasFiles && (
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1}>
            {fileList.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  px: 2,
                  backgroundColor: 'secondary.main',
                  borderRadius: '8px',
                  gap: 1,
                }}
              >
                <Box
                  sx={(theme) => ({
                    borderRadius: 50,
                    p: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.primary.main,
                  })}
                >
                  <Icon name="Document" size="lg" colorScheme="icons.onControls" />
                </Box>
                <Stack spacing="4px" direction="column" sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {placeholder}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`${file.name} - ${formatFileSize(file.size)}`}
                  </Typography>
                </Stack>

                <Box
                  onClick={() => handleRemoveFile(index)}
                  data-testid="delete-file-button"
                  sx={{
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="Cross" size="lg" colorScheme="icons.main" />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}
